"use client";

import { cn } from "@oreme/ui";
import { cva, type VariantProps } from "class-variance-authority";
import Image from "next/image";
import { useLocale } from "next-intl";
import React, { type HTMLAttributes, type ReactElement } from "react";
import { ThemeBadge } from "@/components";
import { ThemeTypeEnum, type Observatory } from "@/types";

const observatoryTileVariants = cva(
  "relative w-40 h-24 p-0 bg-primary-foreground overflow-hidden",
  {
    variants: {
      active: {
        true: "border-primary",
        false: "",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

interface ObservatoryTileProps
  extends
    HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof observatoryTileVariants> {
  observatory: Observatory;
  isActive?: boolean;
}

function ObservatoryTile({
  observatory,
  isActive = false,
  ...props
}: Readonly<ObservatoryTileProps>): ReactElement {
  const locale = useLocale();
  const name = observatory.name[locale];

  return (
    <div
      className={cn(observatoryTileVariants({ active: isActive }))}
      {...props}
    >
      <div className="absolute z-20 top-2 left-2 right-2 flex">
        <ThemeBadge
          theme={observatory.theme as ThemeTypeEnum}
          className="ml-auto"
        />
      </div>
      <figure className="relative h-full bg-gray-600">
        <Image
          alt={name}
          src={observatory.picture!}
          sizes="160px"
          fill
          className="object-cover"
          priority
        />
        <figcaption className="absolute inset-0 z-10 flex flex-col justify-end gap-1 p-2 mt-auto bg-linear-to-b from-transparent to-black/60">
          <h4 className="leading-tight text-xs font-semibold flex-1 flex flex-col items-start justify-end text-left text-white drop-shadow">
            {name}
          </h4>
        </figcaption>
      </figure>
    </div>
  );
}

export { ObservatoryTile, type ObservatoryTileProps };
